import axios from 'axios';
import logger from '../utils/logger';
import { api } from './http';

// General-purpose UPSERT (INSERT ... ON CONFLICT DO UPDATE)
// Supports single-row: { data, conflict_keys: string[], update_columns?: string[] }
// or batch: { columns: string[], values: any[][], conflict_keys: string[], update_columns?: string[] }
export const upsertData = async (
  table_name: string,
  payload: (
    { data: Record<string, any>; conflict_keys: string[]; update_columns?: string[] } |
    { columns: string[]; values: any[][]; conflict_keys: string[]; update_columns?: string[] }
  ),
  database?: string | null
): Promise<{ success: boolean; upserted?: number; ids?: Array<number | string>; errors?: any[] }> => {
  const body: any = { table_name, database, ...payload };
  const endpoint = (database == 'PPMTRAIN' || database == 'DiAPCQS') ? `/sqlExchange/upsert` : '/dbExchange/upsert';
  const response = await api.post(endpoint, body, { headers: { 'Content-Type': 'application/json' } });
  return response.data;
};

// Generic batch update (uniform or per-row distinct)
export const updateDataBatch = async (
  table_name: string,
  options: {
    id_field?: string;
    ids?: Array<number | string>;
    data?: Record<string, any>;
    updates?: Array<{ id: number | string; data: Record<string, any> }>;
    database?: string | null;
  }
): Promise<{ success: boolean; updated?: number; failed?: number; updated_ids?: Array<number | string>; errors?: Array<{ id: number | string; error: string }> }> => {
  const { id_field, ids, data, updates, database } = options || {} as any;
  const payload: any = { table_name, id_field: id_field || 'id', database };
  if (Array.isArray(ids) && data && !updates) {
    payload.ids = ids;
    payload.data = data;
  } else if (Array.isArray(updates) && updates.length) {
    payload.updates = updates;
  } else {
    throw new Error('Invalid batch update options: provide ids+data or updates[]');
  }
  const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/update_batch` : '/dbExchange/update_batch';
  const response = await api.post(endpoint, payload, { headers: { 'Content-Type': 'application/json' } });
  return response.data;
};

export const updateBatchOperations = async (options: {
  operations: Array<
    | {
        table_name: string;
        id_field?: string;
        ids: Array<number | string>;
        data: Record<string, any>;
      }
    | {
        table_name: string;
        id_field?: string;
        updates: Array<{ id: number | string; data: Record<string, any> }>;
      }
  >;
  database?: string | null;
  rollback_on_error?: boolean;
}): Promise<{ success: boolean; updated?: number; failed?: number; results?: any[]; error?: string }> => {
  const { operations, database, rollback_on_error } = options || ({} as any);
  if (!Array.isArray(operations) || operations.length === 0) {
    throw new Error('Invalid updateBatchOperations options: operations[] is required');
  }
  const payload: any = {
    operations,
    database,
    rollback_on_error: rollback_on_error !== undefined ? rollback_on_error : true,
  };
  const endpoint = (database == 'PPMTRAIN' || database == 'DiAPCQS') ? `/sqlExchange/update_batch` : '/dbExchange/update_batch';
  const response = await api.post(endpoint, payload, { headers: { 'Content-Type': 'application/json' } });
  return response.data;
};

export const fetchSqlData = async (table_name: string, field_name?: string, field_value?: any, database?: string|null, page?: number, page_size?: number, search_term?: string) => {
  try {
    const params: any = {
      table_name,
      field_name,
      field_value,
      database,
    };

    // Sayfalama parametrelerini ekle
    if (page !== undefined && page_size !== undefined) {
      params.page = page;
      params.page_size = page_size;
    }

    // Arama parametresini ekle
    if (search_term) {
      params.search_term = search_term;
    }

    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/get` : '/dbExchange/get';
    logger.debug('Fetching data', { context: 'fetchSqlData', data: { endpoint, table: params.table_name } });
    // For very large datasets, disable client timeout
    const isLargeTable = table_name === 'qaqc_pipe_weldingdata_result' || table_name === 'qaqc_pipe_weldingdata_log';
    const requestOptions: any = { params: { ...params } };
    if (isLargeTable) {
      requestOptions.timeout = 0; // do not abort large transfers
    }
    const response = await api.get(endpoint, requestOptions);
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch SQL data', error, { context: 'fetchSqlData' });
    throw error;
  }
};

export const fetchSqlDataWithQuery = async (query_string: string, database?: string|null) => {
  try {
    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/getquery` : '/dbExchange/getquery';
    // Use POST with JSON body to avoid URL length limits with large queries
    const response = await api.post(endpoint, { query_string, database }, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};


export const fetch_between_dates = async (table_name: string, field_name:string, start_date: string, end_date?: string, database?: string |null) => {
  try {
    const params = {
      table_name,  field_name,  start_date,   end_date,  database
    };
    console.log('Fetching data with params with dates:', params);
    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/get_between_dates` : '/dbExchange/get_between_dates';
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching data between dates:', error);
    throw error;
  }
}

export const insertData = async (table_name: string, data: any, database?: string|null) => {
  try {
    let payload: any;

    if (Array.isArray(data)) {
      // Batch mode: convert array of objects to columns/values at ROOT level
      // Collect all unique keys to preserve all columns across heterogeneous rows
      const columnSet = new Set<string>();
      data.forEach((row) => Object.keys(row).forEach(k => columnSet.add(k)));
      const columns = Array.from(columnSet);
      const values = data.map(row => columns.map(col => (row as any)[col] ?? null));

      payload = { table_name, columns, values, database };
    } else {
      // Single row insert behavior (backward compatible)
      payload = { table_name, data, database };
    }

    console.log(`[API INSERT] Sending payload to /dbExchange/insert:`, JSON.stringify(payload, null, 2));
    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/insert` : '/dbExchange/insert';
    const response = await api.post(endpoint, payload,{ headers: { 'Content-Type': 'application/json' } });
    console.log('[API INSERT] Raw Axios Response:', response);
    console.log('[API INSERT] Returning response.data:', response.data)

    const respData: any = response.data;
    if (respData && typeof respData === 'object') {
      const ids = Array.isArray(respData.ids) ? respData.ids : undefined;
      const firstId = ids && ids.length ? ids[0] : undefined;
      if (firstId !== undefined && respData.id === undefined) {
        respData.id = firstId;
      }
      if (firstId !== undefined && respData.inserted_id === undefined) {
        respData.inserted_id = firstId;
      }
    }

    return respData;
  } catch (error) {
    console.error(`Error inserting into ${table_name}:`, error);
    if (axios.isAxiosError(error)) { // More detailed Axios error logging
      console.error(`[API UPDATE] Axios error response:`, error.response?.data);
      console.error(`[API UPDATE] Axios error status:`, error.response?.status);
      console.error(`[API UPDATE] Axios error request payload:`, error.config?.data);
  }
    throw error;
  }
};

// Atomic header + items insert via backend transaction.
// items_fk_column (optional): item column referencing the header's id; the backend
// fills it in automatically after the header insert (useful when that id is a
// serial PK unknown before insert, e.g. it_ag_bagdastiricilari.envanter_id).
export const insertHeaderWithItems = async (payload: {
  header_table: string;
  header_id?: number | string | null;
  header_data: Record<string, any>;
  items_table: string;
  items: Array<Record<string, any>>;
  items_fk_column?: string;
  // Optional: use same backend endpoint shape for upsert variants
  items_conflict_keys?: string[];
  items_update_columns?: string[];
  items_columns?: string[];
  items_values?: any[][];
  database?: string | null;
}) => {
  const response = await api.post('/dbExchange/insert_header_with_items', payload, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 120000,
  });
  return response.data as { header_id: number; item_ids: number[]; success: boolean };
};


// Transactional header update + details batch upsert with explicit conflict handlings
// Posts to the same endpoint as insertHeaderWithItems, but with a richer payload
export const upsertHeaderWithItems = async (payload: {
  header_table: string;
  header_id?: number | null;
  header_data: Record<string, any>;
  items_table: string;
  items_columns: string[];
  items_values: any[][]; // payload should be JSON-stringified in values
  items_conflict_keys: string[];
  items_update_columns: string[];
  database?: string | null;
}) => {
  const { database, ...body } = payload || {} as any;
  const endpoint = (database == 'PPMTRAIN' || database == 'DiAPCQS')
    ? `/sqlExchange/insert_header_with_items`
    : '/dbExchange/insert_header_with_items';
  const response = await api.post(endpoint, { ...body, database }, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 120000,
  });
  return response.data as { success: boolean; header_id?: number | string; upserted?: number };
};


export const insertDataMultipart = async (
  table_name: string,
  data: Record<string, any>,
  files: Record<string, File>,
  database?: string | null
) => {
  const formData = new FormData();
  formData.append('table_name', table_name);

  // Add regular fields (as JSON string for objects)
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, typeof value === 'object' && !(value instanceof File) ? JSON.stringify(value) : value);
  });

  // Add files
  Object.entries(files).forEach(([key, file]) => {
    formData.append(key, file);
  });

  const endpoint = database === 'sql' ? '/sqlExchange/multipart_insert' : '/dbExchange/multipart_insert';
  const response = await api.post(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateData = async (table_name: string, id: number | string, data: any, id_field?: string, database?: string|null): Promise<any> => {
  try {
    // Construct the payload containing table name, record ID, and the update data
    const payload = {
      'table_name': table_name,
      [id_field || 'id']: id, //if id_field is provided, use it, otherwise use 'id'
      'data':data,  // The object containing the fields to be updated
      'database': database
      };
    console.log("[DEBUG] Payload being sent to /dbExchange/update_data:", payload.data)
    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/update_data` : '/dbExchange/update_data';
    const response = await api.put(endpoint, payload,{ headers: { 'Content-Type': 'application/json' } });
    return response.data;
  } catch (error) {
    console.error(`[API UPDATE] Error updating data in table '${table_name}' with ID '${id}':`, error);
    if (axios.isAxiosError(error)) { // More detailed Axios error logging
        console.error(`[API UPDATE] Axios error response:`, error.response?.data);
        console.error(`[API UPDATE] Axios error status:`, error.response?.status);
        console.error(`[API UPDATE] Axios error request payload:`, error.config?.data);
    }
    throw error;
  }
};
export const deleteData = async (table_name: string, field_name: string, field_value: string | number, database?: string|null): Promise<any> => {
  try {
    // Construct the payload containing table name, field name and field value
    const payload = {
      'table_name': table_name,
      'field_name': field_name,
      'field_value': field_value,
      'database': database
      };
    const endpoint = (database =='PPMTRAIN' || database =='DiAPCQS') ? `/sqlExchange/delete` : '/dbExchange/delete';
    const response = await api.delete(endpoint, {data:payload});
    return response.data;
  } catch (error) {
    console.error(`[API DELETE] Error deleting in '${table_name}' field '${field_name}' value '${field_value}':`, error);
    if (axios.isAxiosError(error)) { // More detailed Axios error logging
        console.error(`[API DELETE] Axios error response:`, error.response?.data);
        console.error(`[API DELETE] Axios error status:`, error.response?.status);
        console.error(`[API DELETE] Axios error request payload:`, error.config?.data);
    }
    throw error;
  }
};

export const deleteDataWhere = async (
  table_name: string,
  where: Record<string, any>,
  database?: string | null
): Promise<{ success?: boolean; deleted_count?: number; message?: string; error?: string } | any> => {
  try {
    if (!where || typeof where !== 'object' || Array.isArray(where)) {
      throw new Error('Invalid where clause: expected an object');
    }

    if (database === 'PPMTRAIN' || database === 'DiAPCQS') {
      throw new Error('deleteDataWhere is only supported for Postgres (/dbExchange)');
    }

    const payload = { table_name, where };
    const response = await api.post('/dbExchange/delete_where', payload, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
  } catch (error) {
    console.error(`[API DELETE_WHERE] Error deleting in '${table_name}' where ${JSON.stringify(where)}:`, error);
    if (axios.isAxiosError(error)) {
      console.error(`[API DELETE_WHERE] Axios error response:`, error.response?.data);
      console.error(`[API DELETE_WHERE] Axios error status:`, error.response?.status);
      console.error(`[API DELETE_WHERE] Axios error request payload:`, error.config?.data);
    }
    throw error;
  }
};

export const alterEnumTypes = async (enum_name: string, value: string) => {
  const response = await api.post(
    '/dbExchange/alter_enum_type',
    { enum_name, value },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return response.data;
};
